// ConsentReminderModal.jsx — PR #437
//
// Once-per-day reminder shown on the agent's FIRST dial of the calendar day.
// Reminds the agent that THEY are individually responsible for verbally
// announcing call recording / obtaining consent before proceeding in
// two-party-consent states. The 12 two-party states are visually highlighted.
//
// This is purely an acknowledgement — it does NOT record calls, inject TwiML,
// or gate dialing beyond requiring one click ("I understand") to dismiss. The
// acknowledged date is persisted server-side (per-agent) so it survives a
// device switch; see GET/POST /api/agents/me/consent-reminder*.

import React, { useEffect, useRef } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';

// The 12 two-party (all-party) consent states, highlighted in the modal.
export const TWO_PARTY_CONSENT_STATES = [
  { code: 'CA', name: 'California' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'IL', name: 'Illinois' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MT', name: 'Montana' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NV', name: 'Nevada' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'WA', name: 'Washington' },
];

export default function ConsentReminderModal({ onAcknowledge, busy = false }) {
  const ackRef = useRef(null);

  // Focus the acknowledge button on open. Intentionally NOT dismissible via
  // Escape or backdrop click — the agent must actively acknowledge.
  useEffect(() => {
    ackRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-reminder-title"
      className="fixed inset-0 z-70 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
    >
      <div className="panel p-6 max-w-lg w-full space-y-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-civix-purple shrink-0" />
          <div id="consent-reminder-title" className="font-display font-bold text-lg text-white">
            Call recording &amp; consent reminder
          </div>
        </div>

        <p className="text-sm text-white/70 leading-relaxed">
          You are individually responsible for compliance on your calls. In
          <span className="font-semibold text-white"> two-party (all-party) consent states</span>,
          you must <span className="font-semibold text-white">verbally announce that the call is
          being recorded and obtain the other party's consent</span> before proceeding.
        </p>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-white/50">
            Two-party consent states
          </div>
          <div className="flex flex-wrap gap-2">
            {TWO_PARTY_CONSENT_STATES.map((s) => (
              <span
                key={s.code}
                title={s.name}
                className="px-2.5 py-1 rounded-md text-xs font-semibold bg-civix-purple/20 text-civix-purple border border-civix-purple/40"
              >
                {s.code}
              </span>
            ))}
          </div>
        </div>

        <p className="text-xs text-white/40">
          This reminder appears once per day. Acknowledging confirms you understand
          your recording/consent obligations.
        </p>

        <div className="flex justify-end pt-1">
          <button
            ref={ackRef}
            onClick={onAcknowledge}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-civix-purple/80 hover:bg-civix-purple disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : 'I understand'}
          </button>
        </div>
      </div>
    </div>
  );
}
